import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../context/SettingsContext';
import client from '../api/client';
import {
    Plus, Download, Upload, Filter, Search, Calendar as CalendarIcon,
    Edit2, Trash2, ArrowUpRight, ArrowDownLeft, ChevronLeft, ChevronRight,
    Check, X, Wallet, Briefcase, Tag, TrendingUp, TrendingDown, MoreVertical
} from 'lucide-react';
import TransactionModal from '../components/TransactionModal';
import { useDevice } from '../hooks/useDevice';
import { toast } from 'react-hot-toast';

const Operations = () => {
    const queryClient = useQueryClient();
    const { isMobile } = useDevice();
    const { currency } = useSettings();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTx, setEditingTx] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', type: '', wallet_id: '' });
    const [limit, setLimit] = useState(30);

    // Queries
    const { data: transactions, isLoading: txLoading } = useQuery({
        queryKey: ['transactions', filters, limit],
        queryFn: async () => (await client.get('/transactions', { params: { ...filters, limit } })).data
    });
    const { data: dashboardData } = useQuery({ queryKey: ['dashboard'], queryFn: async () => (await client.get('/dashboard')).data });
    const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: async () => (await client.get('/projects')).data });
    const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: async () => (await client.get('/crm/leads')).data });
    const { data: services } = useQuery({ queryKey: ['services'], queryFn: async () => (await client.get('/products')).data });
    const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: async () => (await client.get('/categories')).data });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/transactions/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions']);
            queryClient.invalidateQueries(['dashboard']);
            toast.success('Удалено');
        }
    });

    const handleSort = (key) => {
        setSortConfig({ key, direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc' });
    };

    const handleExport = async () => {
        try {
            const response = await client.get('/transactions/export', { params: filters, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            toast.success('Экспорт завершен');
        } catch (error) { toast.error("Ошибка экспорта"); }
    };

    const handleAdd = () => { setEditingTx(null); setIsModalOpen(true); };
    const handleEdit = (tx) => { setEditingTx(tx); setIsModalOpen(true); };

    const filteredTransactions = transactions ? transactions.filter(tx => {
        const matchesSearch = (tx.notes?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (tx.category?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            tx.amount.toString().includes(searchTerm);
        return matchesSearch;
    }).sort((a, b) => {
        if (sortConfig.key === 'date') return sortConfig.direction === 'asc' ? new Date(a.date) - new Date(b.date) : new Date(b.date) - new Date(a.date);
        if (sortConfig.key === 'amount') return sortConfig.direction === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        return 0;
    }) : [];

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-12 animate-in fade-in duration-700 pb-20 md:pb-32 px-4">
            {/* Header section with responsive buttons */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8 italic">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">Финансы & Движение</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Million Connections Real-time Operations</p>
                </div>
                <div className="flex gap-2 md:gap-4 flex-wrap">
                    <button onClick={() => document.getElementById('csv-import').click()} className="flex-1 md:flex-none bg-neutral-900 border border-neutral-800 text-neutral-500 px-6 md:px-8 py-4 md:py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[10px] tracking-widest hover:text-white transition-all italic flex items-center justify-center gap-2">
                        <Upload size={18}/> {isMobile ? '' : 'Импорт'}
                    </button>
                    <button onClick={handleExport} className="flex-1 md:flex-none bg-neutral-900 border border-neutral-800 text-neutral-500 px-6 md:px-8 py-4 md:py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[10px] tracking-widest hover:text-white transition-all italic flex items-center justify-center gap-2">
                        <Download size={18}/> {isMobile ? '' : 'Экспорт'}
                    </button>
                    <button onClick={handleAdd} className="flex-[2] md:flex-none bg-blue-600 md:bg-white text-white md:text-black px-6 md:px-10 py-4 md:py-5 rounded-2xl md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-2xl flex items-center justify-center gap-3 italic">
                        <Plus size={20}/> {isMobile ? 'Создать' : 'Новая операция'}
                    </button>
                    <input type="file" id="csv-import" className="hidden" accept=".csv" onChange={() => {}} />
                </div>
            </header>

            {/* Filter section responsive layout */}
            <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 rounded-3xl md:rounded-[4rem] shadow-2xl mb-8 md:mb-12 flex flex-col lg:flex-row gap-6 md:gap-8 italic">
                <div className="relative flex-1">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                    <input 
                        type="text" 
                        placeholder="Поиск по журналу..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        className="w-full bg-neutral-950 border border-neutral-800 text-white pl-16 pr-6 py-5 md:py-6 rounded-2xl md:rounded-[2.5rem] text-sm font-bold outline-none focus:border-blue-500" 
                    />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    <input type="date" value={filters.start_date} onChange={(e) => setFilters({...filters, start_date: e.target.value})} className="bg-neutral-950 border border-neutral-800 text-white p-4 md:p-5 rounded-xl md:rounded-[2rem] text-[10px] md:text-xs font-bold" />
                    <input type="date" value={filters.end_date} onChange={(e) => setFilters({...filters, end_date: e.target.value})} className="bg-neutral-950 border border-neutral-800 text-white p-4 md:p-5 rounded-xl md:rounded-[2rem] text-[10px] md:text-xs font-bold" />
                    <select value={filters.type} onChange={(e) => setFilters({...filters, type: e.target.value})} className="bg-neutral-950 border border-neutral-800 text-white p-4 md:p-5 rounded-xl md:rounded-[2rem] text-[10px] md:text-xs font-bold appearance-none"><option value="">Типы</option><option value="Доход">Доход</option><option value="Расход">Расход</option></select>
                    <select value={filters.wallet_id} onChange={(e) => setFilters({...filters, wallet_id: e.target.value})} className="bg-neutral-950 border border-neutral-800 text-white p-4 md:p-5 rounded-xl md:rounded-[2rem] text-[10px] md:text-xs font-bold appearance-none"><option value="">Кошельки</option>{dashboardData?.wallets?.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select>
                </div>
            </div>

            {/* Content display area */}
            <div className="relative min-h-[400px]">
                {txLoading ? (
                    <div className="flex flex-col items-center justify-center py-40 gap-4 opacity-50">
                        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Синхронизация базы...</p>
                    </div>
                ) : (
                    <>
                        {/* Mobile view cards - Hidden on md and up */}
                        <div className="grid grid-cols-1 gap-4 md:hidden">
                            {filteredTransactions.map(tx => (
                                <div 
                                    key={tx.id} 
                                    onClick={() => handleEdit(tx)} 
                                    className="bg-neutral-900 border border-neutral-800 p-6 rounded-[2.5rem] relative overflow-hidden active:scale-[0.98] transition-all italic"
                                >
                                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${tx.type === 'Доход' ? 'bg-emerald-500' : tx.type === 'Перевод' ? 'bg-blue-500' : 'bg-rose-500'}`} />
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-white font-black text-base uppercase leading-tight italic">{tx.category}</h3>
                                            <p className="text-[9px] text-neutral-500 font-bold uppercase tracking-widest italic mt-1">{tx.notes || 'Нет описания'}</p>
                                        </div>
                                        <div className={`text-lg font-black italic ${tx.type === 'Доход' ? 'text-emerald-500' : tx.type === 'Перевод' ? 'text-blue-500' : 'text-rose-500'}`}>
                                            {tx.type === 'Доход' ? '+' : '-'}{tx.amount.toLocaleString()} {currency}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center pt-4 border-t border-white/5">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[8px] font-black text-neutral-600 uppercase flex items-center gap-1 italic"><Wallet size={10}/> {tx.wallet_name}</span>
                                            {tx.project_name && <span className="text-[8px] font-black text-blue-500 uppercase flex items-center gap-1 italic"><Tag size={10}/> {tx.project_name}</span>}
                                        </div>
                                        <span className="text-[8px] font-black text-neutral-700 italic">{new Date(tx.date).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop view table - Hidden on sm */}
                        <div className="hidden md:block bg-neutral-900 border border-neutral-800 rounded-[4rem] shadow-2xl overflow-hidden italic">
                            <table className="w-full text-left">
                                <thead className="bg-neutral-950/50">
                                    <tr>
                                        <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Дата / Время</th>
                                        <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Категория</th>
                                        <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest text-center">Кошелек</th>
                                        <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest text-right">Сумма</th>
                                        <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest text-right text-transparent">Управление</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {filteredTransactions.map(tx => (
                                        <tr key={tx.id} className="hover:bg-neutral-800/30 transition-all group cursor-pointer" onClick={() => handleEdit(tx)}>
                                            <td className="px-10 py-8">
                                                <p className="text-lg font-black text-white italic tracking-tighter">{new Date(tx.date).toLocaleDateString()}</p>
                                                <p className="text-[10px] text-neutral-500 font-bold uppercase mt-1 italic">{new Date(tx.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                                            </td>
                                            <td className="px-10 py-8">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-2 h-10 rounded-full ${tx.type === 'Доход' ? 'bg-emerald-500' : tx.type === 'Перевод' ? 'bg-blue-500' : 'bg-rose-500'}`} />
                                                    <div>
                                                        <p className="text-lg font-black text-white italic uppercase leading-none mb-1">{tx.category}</p>
                                                        <p className="text-[10px] text-neutral-500 font-bold uppercase italic tracking-widest">{tx.notes || 'ID: '+tx.id}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-10 py-8 text-center">
                                                <span className="bg-black/40 border border-neutral-800 text-neutral-400 px-4 py-2 rounded-xl text-[10px] font-black uppercase italic tracking-widest">{tx.wallet_name}</span>
                                            </td>
                                            <td className={`px-10 py-8 text-right font-black italic text-xl ${tx.type === 'Доход' ? 'text-emerald-500' : tx.type === 'Перевод' ? 'text-blue-500' : 'text-rose-500'}`}>
                                                {tx.type === 'Доход' ? '+' : '-'}{tx.amount.toLocaleString()} {currency}
                                            </td>
                                            <td className="px-10 py-8 text-right">
                                                <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all">
                                                    <button onClick={(e) => { e.stopPropagation(); handleEdit(tx); }} className="w-12 h-12 bg-blue-600/10 text-blue-500 hover:bg-blue-600 hover:text-white rounded-xl flex items-center justify-center transition-all shadow-xl"><Edit2 size={18}/></button>
                                                    <button onClick={(e) => { e.stopPropagation(); if(window.confirm('Удалить?')) deleteMutation.mutate(tx.id); }} className="w-12 h-12 bg-rose-500/5 text-rose-500 hover:bg-rose-600 hover:text-white rounded-xl flex items-center justify-center transition-all shadow-xl"><Trash2 size={18}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            <TransactionModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingTx(null); }}
                onSuccess={() => {
                    queryClient.invalidateQueries(['transactions']);
                    queryClient.invalidateQueries(['dashboard']);
                    setIsModalOpen(false);
                    setEditingTx(null);
                }}
                initialData={editingTx}
                wallets={dashboardData?.wallets}
                categories={categories}
                projects={projects}
                clients={clients}
                services={services}
            />
        </div>
    );
};

export default Operations;
